import { MetricWindow, ProposedAction, Snapshot, WindowKey } from './types';

// Windows are ALWAYS shown in this order now (Yesterday included every run).
export const WINDOW_ORDER: WindowKey[] = ['today', 'yesterday', 'd7', 'd14'];
export const WINDOW_LABELS: Record<WindowKey, string> = {
  today: 'T',
  yesterday: 'Y',
  d7: '7d',
  d14: '14d',
};
export const PRIMARY_WINDOW: WindowKey = 'd7'; // default window used for sorting
export const EMPHASIS_WINDOW: WindowKey = 'today'; // bolded in each cell

export const DEFAULT_TRUE_TARGET = 2.5; // DCOP fallback

// Premium's formatting and thresholds are shared with the Creative Performance
// dashboard, which reports the same metric — see src/utils/premium. Re-exported
// here so this page's own imports keep working.
export {
  DEFAULT_PREMIUM_MAX,
  fmtPremium,
  premiumTone,
} from '../../utils/premium';

export const dash = '–';

export function fmtRoas(v: unknown): string {
  return typeof v === 'number' && isFinite(v) ? v.toFixed(2) : dash;
}
export function fmtMoney(v: unknown): string {
  if (typeof v !== 'number' || !isFinite(v)) return dash;
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
}

export type Tone = 'good' | 'bad' | '';

// Color tone for a metric value vs its target. Green = at/above goal, red = clearly
// out of range; neutral otherwise. Spend never colors.
export function trueTone(v: number | null | undefined, target: number): Tone {
  if (typeof v !== 'number') return '';
  if (v >= target) return 'good';
  if (v < target * 0.6) return 'bad';
  return '';
}

export const windowVal = (
  w: Partial<Record<WindowKey, MetricWindow>>,
  key: WindowKey,
  metric: keyof MetricWindow,
): number | null => {
  const cell = w[key];
  const v = cell ? cell[metric] : null;
  return typeof v === 'number' ? v : null;
};

// ---- proposed actions grouping ---------------------------------------------
export const ACTION_GROUPS: { type: ProposedAction['type']; label: string }[] = [
  { type: 'pause_ad', label: 'Pause ads' },
  { type: 'raise_budget', label: 'Raise budget' },
  { type: 'loosen_cap', label: 'Loosen cap' },
  { type: 'promote', label: 'Promote' },
  { type: 'flag', label: 'Flag / decide' },
];

export function groupActions(actions: ProposedAction[]) {
  return ACTION_GROUPS.map(g => ({
    ...g,
    actions: actions.filter(a => a.type === g.type),
  })).filter(g => g.actions.length > 0);
}

// ---- actions ↔ tables consistency ------------------------------------------
// The backend derives every proposed action from a table verdict (scale →
// raise_budget, pause → pause_ad, promote → promote), so an open action with no
// matching table row — or a promotion pointing outside a testing campaign —
// means something is off (miscategorized ad or stale data) and should be
// investigated, not approved. Only meaningful on schema v3+ snapshots.
export function actionTableMismatches(
  actions: ProposedAction[],
  snapshot: Snapshot | null,
): string[] {
  if (!snapshot || (snapshot.schema_version ?? 0) < 3) return [];
  const expected = new Set<string>();
  (snapshot.winners ?? []).forEach(a => {
    if (a.verdict === 'scale' && a.adset_id) expected.add(`raise_budget:${a.adset_id}`);
  });
  (snapshot.decisions ?? []).forEach(a => {
    if (a.verdict === 'pause' && a.ad_id) expected.add(`pause_ad:${a.ad_id}`);
    if (a.verdict === 'promote' && a.ad_id) expected.add(`promote:${a.ad_id}`);
  });
  (snapshot.testing_past_3x ?? []).forEach(a => {
    if (a.ad_id) expected.add(`pause_ad:${a.ad_id}`);
  });
  const msgs: string[] = [];
  actions.forEach(a => {
    // Decided items legitimately outlive their table rows (visible for 24h).
    if ((a.status || 'proposed') !== 'proposed') return;
    const name = a.ad_name || a.campaign_name || a.action_id;
    if (a.type === 'promote' && !(a.campaign_name || '').toUpperCase().includes('TESTING')) {
      msgs.push(
        `"${name}" is proposed for promotion but is not in a testing campaign — ` +
          'promotions only apply to testing ads, so this ad is likely miscategorized.',
      );
    } else if (!expected.has(a.action_id)) {
      msgs.push(
        `"${name}" (${a.type.replace('_', ' ')}) has no matching verdict in the tables above.`,
      );
    }
  });
  return msgs;
}
